/**
 * Online Appointments — scheduling window helpers (2026-09-04).
 *
 * The office sets a Start Date and an End Date for online booking. With
 * "Automatically extend the booking window" on, the End Date is no longer a stored
 * date at all: it is recomputed every day as "the same day of the month, N months
 * ahead of today", clamped to the target month's last day. The stored End Date is
 * kept untouched so unchecking the box restores it.
 *
 * The SERVER is the authority (it computes this in the office's timezone and enforces
 * it on availability and booking). This module exists so the admin tab can show the
 * office the resulting date LIVE, as they change the dropdown, without a save+reload —
 * which means the two implementations must agree. `addMonthsClamped` here and
 * `addMonthsClamped` in online-scheduling-service/scheduling-window.js are the same
 * algorithm, pinned by one shared clamping table in both test suites
 * (cypress/node-tests/scheduling-window-util.test.js).
 *
 * IMPORT-FREE ON PURPOSE: cypress/node-tests loads this exact file through
 * helpers/load-esm-util.js and executes it, so the tests can never drift from the
 * shipped code. Adding an import breaks that loader.
 *
 * All date math is done on 'YYYY-MM-DD' strings with Date.UTC — never `new Date(ymd)`,
 * which parses as UTC midnight and then reads back a day earlier west of Greenwich.
 */

export const AUTO_EXTEND_MIN_MONTHS = 1;
export const AUTO_EXTEND_MAX_MONTHS = 6;
export const AUTO_EXTEND_MONTH_OPTIONS = [1, 2, 3, 4, 5, 6];
export const AUTO_EXTEND_DEFAULT_MONTHS = 2;

/** Shown when a rolling save is refused. Mirrors the backend's 400 message. */
export const INVALID_MONTHS_MESSAGE =
  'Automatically extend the booking window requires a whole number of months from 1 to 6.';

/** Shown when auto-extend is off and Start Date is later than End Date (pre-existing rule). */
export const START_AFTER_END_MESSAGE =
  'Scheduling Window: Start Date cannot be after End Date.';

/** Offered when the window is about to close on its own. */
export const AUTO_EXTEND_NUDGE =
  'Tip: turn on automatic extension so the window never closes.';

/** Days of grace before we suggest auto-extend on a fixed End Date. */
export const NUDGE_DAYS_AHEAD = 14;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function pad(n, width) {
  let s = String(n);
  while (s.length < width) s = '0' + s;
  return s;
}

/** Last day (28-31) of a 0-based month in a year. */
function lastDayOfMonth(year, monthIndex) {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/** 'YYYY-MM-DD' that is also a real calendar day (rejects 2026-02-30, 2027-02-29). */
export function isYmd(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= lastDayOfMonth(year, month - 1);
}

/**
 * Same day-of-month N months ahead, clamped to the target month's last day.
 * '2026-01-31' + 1 => '2026-02-28'.  '2028-01-31' + 1 => '2028-02-29'.
 * Throws rather than guessing: a silent fallback here would quietly hand patients
 * a booking window the office never chose.
 */
export function addMonthsClamped(ymd, months) {
  if (!isYmd(ymd)) {
    throw new Error(`addMonthsClamped: expected YYYY-MM-DD, got ${JSON.stringify(ymd)}`);
  }
  if (typeof months !== 'number' || !Number.isInteger(months)) {
    throw new Error(`addMonthsClamped: months must be a whole number, got ${JSON.stringify(months)}`);
  }
  const year = Number(ymd.slice(0, 4));
  const month = Number(ymd.slice(5, 7));
  const day = Number(ymd.slice(8, 10));

  const absoluteMonth = (month - 1) + months;
  const targetYear = year + Math.floor(absoluteMonth / 12);
  const targetMonthIndex = ((absoluteMonth % 12) + 12) % 12;
  const targetDay = Math.min(day, lastDayOfMonth(targetYear, targetMonthIndex));

  return `${pad(targetYear, 4)}-${pad(targetMonthIndex + 1, 2)}-${pad(targetDay, 2)}`;
}

/**
 * Coerce whatever the <select> / stored JSON hands us into an integer 1..6, else null.
 * A string '2' must NOT be treated as invalid — that would turn the feature off for an
 * office that had it on. Booleans are rejected explicitly (Number(true) === 1).
 */
export function normalizeAutoExtendMonths(value) {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'object') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n < AUTO_EXTEND_MIN_MONTHS || n > AUTO_EXTEND_MAX_MONTHS) return null;
  return n;
}

/** Today in the BROWSER's timezone as 'YYYY-MM-DD' (fallback when the server's day is unknown). */
export function localTodayYmd(now = new Date()) {
  return `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1, 2)}-${pad(now.getDate(), 2)}`;
}

/** Whole days from `fromYmd` to `toYmd` (negative when `toYmd` is earlier). */
function daysBetween(fromYmd, toYmd) {
  const a = Date.UTC(Number(fromYmd.slice(0, 4)), Number(fromYmd.slice(5, 7)) - 1, Number(fromYmd.slice(8, 10)));
  const b = Date.UTC(Number(toYmd.slice(0, 4)), Number(toYmd.slice(5, 7)) - 1, Number(toYmd.slice(8, 10)));
  return Math.round((b - a) / 86400000);
}

/**
 * The End Date patients actually get.
 * Rolling wins over the stored End Date; an invalid month count falls back to the
 * stored End Date (never to "no limit by accident").
 * `todayYmd` should be the OFFICE's day (effectiveSchedulingWindow.todayYmd); the
 * browser's day is only a fallback for an old backend that sends no sibling field.
 */
export function resolveEffectiveEnd({ autoExtend, months, fixedEnd, todayYmd } = {}) {
  if (autoExtend === true) {
    const n = normalizeAutoExtendMonths(months);
    if (n !== null) {
      return addMonthsClamped(isYmd(todayYmd) ? todayYmd : localTodayYmd(), n);
    }
  }
  return isYmd(fixedEnd) ? fixedEnd : null;
}

/**
 * Refuse a scheduling window that can never fire, before it is stored.
 * With auto-extend ON the start<=end check is deliberately SKIPPED: the stored End
 * Date is ignored while rolling, and it is usually in the past by the time an office
 * turns the feature on. A future Start Date beyond the rolling End is legal — it
 * self-heals as the window rolls forward — and is warned about in the UI instead.
 */
export function validateSchedulingWindow(local) {
  const l = local || {};
  if (l.schedulingWindowAutoExtend === true) {
    if (normalizeAutoExtendMonths(l.schedulingWindowAutoExtendMonths) === null) {
      return { ok: false, message: INVALID_MONTHS_MESSAGE };
    }
    return { ok: true };
  }
  if (l.schedulingWindowStart && l.schedulingWindowEnd && l.schedulingWindowStart > l.schedulingWindowEnd) {
    return { ok: false, message: START_AFTER_END_MESSAGE };
  }
  return { ok: true };
}

/**
 * Suggest auto-extend when the fixed window is blank, already past, or about to lapse.
 * Returns the tip text, or null when there is nothing to say.
 */
export function schedulingWindowNudge(local, todayYmd) {
  const l = local || {};
  if (l.schedulingWindowAutoExtend === true) return null;
  const end = l.schedulingWindowEnd;
  if (!end) return AUTO_EXTEND_NUDGE;
  if (!isYmd(end)) return null; // malformed stored value: a different problem
  const today = isYmd(todayYmd) ? todayYmd : localTodayYmd();
  return daysBetween(today, end) <= NUDGE_DAYS_AHEAD ? AUTO_EXTEND_NUDGE : null;
}

/** 'November 4, 2026' — locale-independent so the hint reads the same everywhere. */
export function formatYmdLong(ymd) {
  if (!isYmd(ymd)) return '';
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10))));
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

// ---------------------------------------------------------------------------
// Minimum notice (2026-09-18) — "patients can't book a time that starts within N hours"
// ---------------------------------------------------------------------------
//
// Stored as ONE integer, `minimumNoticeHours` (0..720, 0/absent = no minimum). The office types
// a number and picks Hours or Days; Days is purely × 24. The SERVER enforces it (availability
// hides earlier slots, the booking write refuses them) in the office's timezone — this block
// only converts the admin inputs and writes the labels. `normalizeMinimumNoticeHours` and
// `formatMinimumNoticeLabel` are the same algorithm as online-scheduling-service/
// scheduling-window.js, pinned by the SHARED tables in cypress/node-tests/scheduling-window-util.test.js.

export const MINIMUM_NOTICE_MAX_HOURS = 720;
export const MINIMUM_NOTICE_MAX_DAYS = 30;

/** Shown when the office types something the server would refuse. */
export const INVALID_MINIMUM_NOTICE_MESSAGE =
  'Minimum notice must be a whole number from 0 to 720 hours (30 days).';

/**
 * Stored/posted value → integer 0..720, else null. Absent/blank → 0 (OFF). Digit strings are
 * accepted ('24'); booleans, objects, negatives, decimals, '1e1', '0x18' are refused.
 */
export function normalizeMinimumNoticeHours(value) {
  if (value === null || value === undefined) return 0;
  let n;
  if (typeof value === 'number') {
    n = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    if (!/^\d+$/.test(trimmed)) return null;
    n = Number(trimmed);
  } else {
    return null;
  }
  if (!Number.isInteger(n)) return null;
  if (n < 0 || n > MINIMUM_NOTICE_MAX_HOURS) return null;
  return n;
}

/** '24 hours', '36 hours', '2 days' — whole days only from 48 h up. '' when OFF or invalid. */
export function formatMinimumNoticeLabel(hours) {
  const n = normalizeMinimumNoticeHours(hours);
  if (!n) return '';
  if (n >= 48 && n % 24 === 0) return `${n / 24} days`;
  return n === 1 ? '1 hour' : `${n} hours`;
}

/**
 * The admin inputs (number box + Hours/Days select) → stored hours, else null.
 * Blank → 0. The cap is applied AFTER the unit conversion, so 31 days (744 h) is refused.
 */
export function minimumNoticeInputToHours(value, unit) {
  if (value === null || value === undefined) return 0;
  let n;
  if (typeof value === 'number') {
    n = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    if (!/^\d+$/.test(trimmed)) return null;
    n = Number(trimmed);
  } else {
    return null;
  }
  if (!Number.isInteger(n) || n < 0) return null;
  if (unit === 'days') return normalizeMinimumNoticeHours(n * 24);
  if (unit === 'hours') return normalizeMinimumNoticeHours(n);
  return null;
}

/**
 * Stored hours → the admin inputs. Uses the SAME rule as the label, so what the office sees in
 * the box after a reload reads the same as the help line: 24 → 24 Hours, 48 → 2 Days.
 */
export function splitMinimumNoticeHours(hours) {
  const n = normalizeMinimumNoticeHours(hours);
  if (!n) return { value: '', unit: 'hours' };
  if (n >= 48 && n % 24 === 0) return { value: n / 24, unit: 'days' };
  return { value: n, unit: 'hours' };
}

/** Save-time check: { ok: true, hours } or { ok: false, message }. */
export function validateMinimumNoticeInput(value, unit) {
  const hours = minimumNoticeInputToHours(value, unit);
  if (hours === null) return { ok: false, message: INVALID_MINIMUM_NOTICE_MESSAGE };
  return { ok: true, hours };
}

/** The live help line under the inputs; null while the input is invalid (the warning shows instead). */
export function minimumNoticeHelpText(value, unit) {
  const hours = minimumNoticeInputToHours(value, unit);
  if (hours === null) return null;
  const label = formatMinimumNoticeLabel(hours);
  if (!label) return 'No minimum — patients can book any open time, even later today. Leave blank to keep it that way.';
  return `Patients can't book a time that starts within the next ${label} (office time). They'll see a note asking them to call you instead.`;
}
